(() => {
  let inFlight = false;

  const PRESERVE_IDS = ["beatPlayerBar", "licenseModal"];

  // ── Cache + préchargement (la clé de la rapidité) ─────────────────
  // On garde en mémoire le HTML des pages déjà récupérées, et on les
  // précharge AU SURVOL d'un lien. Résultat : au clic, la page est déjà
  // prête => navigation quasi instantanée, sans rien changer d'autre.
  const CACHE = new Map();        // url -> { html, ts }
  const PREFETCHING = new Set();  // urls en cours de préchargement
  const CACHE_TTL = 90000;        // fraîcheur 90s : garde les pages en mémoire plus
                                  // longtemps => aller-retour entre pages = instantané

  function cacheGet(url) {
    const e = CACHE.get(url);
    if (!e) return null;
    if (Date.now() - e.ts > CACHE_TTL) {
      CACHE.delete(url);
      return null;
    }
    return e.html;
  }

  function cacheSet(url, html) {
    CACHE.set(url, { html, ts: Date.now() });
    // On limite la taille du cache pour ne pas gonfler la mémoire
    if (CACHE.size > 20) {
      const firstKey = CACHE.keys().next().value;
      CACHE.delete(firstKey);
    }
  }

  async function fetchHtml(url) {
    // Sécurité: un chargement ne peut plus rester bloqué indéfiniment
    // (sur mobile, une requête peut "geler"). Au-delà de 10s on abandonne.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 10000);
    try {
      const res = await fetch(url, {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.text();
    } finally {
      clearTimeout(timer);
    }
  }

  function prefetch(url) {
    if (cacheGet(url) || PREFETCHING.has(url)) return;
    PREFETCHING.add(url);
    fetchHtml(url)
      .then((html) => cacheSet(url, html))
      .catch(() => {})
      .finally(() => PREFETCHING.delete(url));
  }

  function isModifiedClick(e) {
    return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
  }

  function shouldHandleLink(a) {
    if (!a) return false;

    if (a.hasAttribute("data-no-softnav")) return false;
    if (a.target && a.target.toLowerCase() !== "_self") return false;
    if (a.hasAttribute("download")) return false;

    const href = a.getAttribute("href");
    if (!href) return false;
    if (href.startsWith("#")) return false;
    if (href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("javascript:")) return false;

    const url = new URL(a.href, window.location.href);
    if (url.origin !== window.location.origin) return false;

    // Shopify pages à éviter en soft-nav
    if (
      url.pathname.startsWith("/cart") ||
      url.pathname.startsWith("/checkout") ||
      url.pathname.startsWith("/account")
    ) {
      return false;
    }

    // hash-only on same page => laisse normal
    const cur = new URL(window.location.href);
    if (url.pathname === cur.pathname && url.search === cur.search && url.hash) return false;

    return true;
  }

  function preserveNodes() {
    const preserved = {};
    PRESERVE_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        preserved[id] = el;
        // On le sort du MainContent pour qu'il ne soit jamais détruit
        document.body.appendChild(el);
      }
    });
    return preserved;
  }

  function restoreNodes(preserved) {
    Object.entries(preserved).forEach(([id, el]) => {
      if (!el) return;

      // Si la nouvelle page contient un "placeholder" du même id, on le remplace
      const placeholder = document.getElementById(id);
      if (placeholder && placeholder !== el) {
        placeholder.replaceWith(el);
      } else if (!document.body.contains(el)) {
        document.body.appendChild(el);
      }
    });
  }

  function runSoftnavScripts(root) {
    // On n'exécute QUE les scripts explicitement marqués
    const scripts = root.querySelectorAll('script[data-softnav]');

    scripts.forEach((oldScript) => {
      const type = oldScript.getAttribute('type') || '';

      // Ignore les scripts "data" (JSON/ld+json/etc.)
      const allowed = ['', 'text/javascript', 'application/javascript', 'module'];
      if (!allowed.includes(type)) return;

      // On évite de recharger des scripts externes ici
      if (oldScript.src) return;

      const s = document.createElement('script');
      if (type) s.type = type;

      const nonce = oldScript.getAttribute('nonce');
      if (nonce) s.setAttribute('nonce', nonce);

      s.textContent = oldScript.textContent;
      oldScript.replaceWith(s); // => exécution immédiate
    });
  }

  // ── Barre de chargement en haut (retour visuel immédiat au clic) ──
  // Montre tout de suite que le clic a été pris en compte, pour éviter
  // de retaper plusieurs fois quand une page met du temps à charger.
  let progressEl = null;
  let progressTimer = null;
  function startProgress() {
    if (!progressEl) {
      progressEl = document.createElement("div");
      progressEl.style.cssText =
        "position:fixed;top:0;left:0;height:3px;width:0;z-index:99999;" +
        "background:#ff0000;box-shadow:0 0 8px rgba(255,0,0,.6);" +
        "opacity:0;pointer-events:none;";
      (document.body || document.documentElement).appendChild(progressEl);
    }
    clearTimeout(progressTimer);
    progressEl.style.transition = "none";
    progressEl.style.width = "0";
    progressEl.style.opacity = "1";
    void progressEl.offsetWidth; // force le reflow
    progressEl.style.transition = "width .4s ease, opacity .3s ease";
    progressEl.style.width = "70%";
  }
  function finishProgress() {
    if (!progressEl) return;
    progressEl.style.width = "100%";
    progressTimer = setTimeout(() => {
      progressEl.style.opacity = "0";
      progressEl.style.width = "0";
    }, 250);
  }

  async function navigate(url, { replace = false, scrollTop = true } = {}) {
    // Si le verrou est (anormalement) encore actif, on NE reste pas coincé :
    // on ouvre la page normalement pour que le clic fasse toujours quelque chose.
    if (inFlight) {
      window.location.href = url;
      return;
    }
    inFlight = true;
    startProgress();

    const preserved = preserveNodes();

    try {
      // ⚡ Si la page est déjà préchargée (survol), on l'utilise direct.
      let html = cacheGet(url);
      if (!html) {
        html = await fetchHtml(url);
        cacheSet(url, html);
      }

      const doc = new DOMParser().parseFromString(html, "text/html");

      const newMain = doc.querySelector("main#MainContent");
      const curMain = document.querySelector("main#MainContent");
      if (!newMain || !curMain) throw new Error("MainContent introuvable");

      // Title + body class (important sur Dawn)
      document.title = doc.title || document.title;
      document.body.className = doc.body.className;

      // Swap content — on ADOPTE les nœuds déjà analysés au lieu de tout
      // ré-analyser avec innerHTML (qui re-parse 2 fois). Résultat :
      // changement de page plus léger/rapide, surtout pour les pages lourdes
      // comme "À propos" (grosses sections FAQ avec beaucoup de CSS intégré).
      while (curMain.firstChild) curMain.removeChild(curMain.firstChild);
      while (newMain.firstChild) curMain.appendChild(newMain.firstChild);

      // History
      if (replace) history.replaceState({}, "", url);
      else history.pushState({}, "", url);

      // 🤍 Déplace le trait blanc du menu PILE au changement de page (synchronisé),
      // AVANT le gros travail de réinitialisation => l'animation ne se coupe plus.
      sasFollowActiveNav();

      // 👁️ Révèle TOUT DE SUITE les éléments déjà visibles (FAQ, réseaux, etc.)
      // pour qu'ils apparaissent dès le changement de page, sans attendre la
      // réinitialisation (surtout utile sur la page "À propos", la plus lourde).
      try {
        curMain.querySelectorAll(".sas-reveal:not(.is-sas-visible)").forEach((el) => {
          const r = el.getBoundingClientRect();
          if (r.bottom > 0 && r.top < (window.innerHeight || 0) && r.width > 0) {
            el.classList.add("is-sas-visible");
          }
        });
      } catch (e) {}

      // Restore le player + modal
      restoreNodes(preserved);

      // Lance les scripts des sections marqués data-softnav
      runSoftnavScripts(curMain);

      if (scrollTop) {
        // ⚠️ Le menu burger verrouille le défilement (overflow-hidden) tant
        // qu'il est ouvert. Tant que c'est verrouillé, "remonter en haut" n'a
        // AUCUN effet. On retire donc le VERROU de scroll tout de suite.
        document.body.classList.remove("overflow-hidden", "overflow-hidden-mobile", "overflow-hidden-tablet");
        document.documentElement.classList.remove("overflow-hidden", "overflow-hidden-mobile", "overflow-hidden-tablet");
        document.body.style.position = "";
        document.body.style.top = "";

        // Saut INSTANTANÉ vers le haut (pas de défilement doux).
        const jumpTop = () => {
          const de = document.documentElement;
          const prev = de.style.scrollBehavior;
          de.style.scrollBehavior = "auto";
          window.scrollTo(0, 0);
          de.style.scrollBehavior = prev;
        };

        // ✅ Si le MENU BURGER est resté ouvert (ex: on clique sur le LOGO pendant
        //    que le menu est ouvert), on le ferme en FONDU. On GARDE "sas-menu-open"
        //    pendant le fondu (sinon le tiroir perd son plein écran), et on ne le
        //    retire qu'À LA FIN, en même temps que [open].
        const sasDet = document.getElementById("Details-menu-drawer-container");
        const sasMenuOpen = sasDet && sasDet.hasAttribute("open");

        if (!sasMenuOpen) {
          // Pas de menu ouvert => on retire sas-menu-open immédiatement.
          document.body.classList.remove("sas-menu-open");
          document.documentElement.classList.remove("sas-menu-open");
        }

        jumpTop();
        requestAnimationFrame(jumpTop);

        if (sasMenuOpen) {
          const sasDrawer = sasDet.querySelector(".menu-drawer");
          if (sasDrawer) sasDrawer.classList.add("is-closing"); // fondu de sortie
          const sasSum = sasDet.querySelector("summary");
          if (sasSum) sasSum.setAttribute("aria-expanded", "false"); // icône croix -> 3 traits
          setTimeout(() => {
            sasDet.removeAttribute("open");
            if (sasDrawer) sasDrawer.classList.remove("is-closing");
            document.body.classList.remove("sas-menu-open");
            document.documentElement.classList.remove("sas-menu-open");
            // On reste bien en haut de la nouvelle page.
            jumpTop();
            requestAnimationFrame(jumpTop);
          }, 200);
        }
      }

      // Event de refresh pour les scripts (beat-store etc.)
      const ev = new CustomEvent("softnav:loaded", { detail: { url } });
      window.dispatchEvent(ev);
      document.dispatchEvent(ev);
    } catch (err) {
      console.error("[softnav] error:", err);
      window.location.href = url; // fallback normal
    } finally {
      inFlight = false;
      finishProgress();
    }
  }

  // ── Préchargement au survol / toucher / focus clavier ─────────────
  function maybePrefetchFromEvent(e) {
    const a = e.target.closest && e.target.closest("a");
    if (a && shouldHandleLink(a)) { prefetch(a.href); return; }

    // Bouton panier €34,99 d'une ligne (qui ouvre la page produit mais n'est
    // pas un lien) => on précharge quand même sa page produit au survol/toucher.
    const cart = e.target.closest && e.target.closest(".beat-cart-btn");
    if (cart) {
      const beat = cart.closest(".beat-item[data-product-id]");
      const handle = beat && beat.getAttribute("data-product-id");
      if (handle) prefetch(location.origin + "/products/" + handle);
    }
  }

  // Souris (desktop) : dès que le curseur passe sur un lien
  document.addEventListener("mouseover", maybePrefetchFromEvent, { capture: true, passive: true });
  // Clavier : quand un lien reçoit le focus
  document.addEventListener("focusin", maybePrefetchFromEvent, { capture: true, passive: true });
  // Mobile : au tout début du toucher (préchargement + on mémorise la position)
  let __sasTapXY = null;
  document.addEventListener("touchstart", (e) => {
    maybePrefetchFromEvent(e);
    const t = e.touches && e.touches[0];
    __sasTapXY = t ? { x: t.clientX, y: t.clientY } : null;
  }, { capture: true, passive: true });

  // 📱 Navigation TACTILE fiable. Sur mobile, le "click" qui suit un tap est
  // parfois avalé (le lien s'éclaire mais la page ne s'ouvre pas => il faut
  // taper 2 fois). On gère donc le tap PROPRE directement via touchend, pour
  // les liens de navigation du site (en-tête, pied de page).
  let __sasLastTouchNav = 0;
  document.addEventListener("touchend", (e) => {
    const a = e.target.closest && e.target.closest("a");
    if (!shouldHandleLink(a)) return;

    // On NE touche PAS aux liens internes du beat store ni au menu burger :
    // ils ont déjà leur propre logique.
    if (
      a.closest(".beat-store-section") ||
      a.closest("#menu-drawer, menu-drawer, .menu-drawer, .menu-drawer-container") ||
      a.classList.contains("beat-tag") ||
      a.classList.contains("beat-title-link")
    ) return;

    // Vrai tap ? (si le doigt a bougé, c'était un scroll => on ignore)
    const t = e.changedTouches && e.changedTouches[0];
    if (__sasTapXY && t && (Math.abs(t.clientX - __sasTapXY.x) > 12 || Math.abs(t.clientY - __sasTapXY.y) > 12)) return;

    e.preventDefault();            // empêche le clic fantôme => pas de double navigation
    __sasLastTouchNav = Date.now();
    if (inFlight) { window.location.href = a.href; return; }
    navigate(a.href);
  }, { capture: true });

  document.addEventListener(
    "click",
    (e) => {
      if (e.defaultPrevented || isModifiedClick(e)) return;
      const a = e.target.closest("a");
      if (!shouldHandleLink(a)) return;

      // ⚠️ Si une navigation est déjà en cours, on NE bloque PAS le clic :
      // on laisse le navigateur ouvrir la page normalement.
      if (inFlight) return;

      e.preventDefault();
      navigate(a.href);
    },
    true
  );

  window.addEventListener("popstate", () => {
    navigate(window.location.href, { replace: true, scrollTop: false });
  });

  // 🔓 Sécurité mobile : si l'onglet a été mis en veille / réaffiché (ou
  // restauré depuis le cache du navigateur), on relâche le verrou pour que
  // les clics suivants fonctionnent toujours. Corrige le bug "je tape sur
  // un lien du menu et rien ne se passe" après être revenu sur l'onglet.
  function releaseLock() { inFlight = false; }
  window.addEventListener("pageshow", releaseLock);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") releaseLock();
  });

  // Permet à d'autres scripts (beat-store.js) d'utiliser la navigation soft
  window.softNavigate = (url, opts) => navigate(url, opts);
  // Permet à d'autres scripts de précharger une page (ex: bouton €34,99 du lecteur)
  window.__sasPrefetch = (url) => { try { prefetch(url); } catch (e) {} };

  // ── Préchargement automatique "au repos" (surtout utile sur MOBILE) ──
  // Sur mobile il n'y a pas de survol : on précharge donc les liens visibles
  // (le menu BEATZ / À PROPOS...) quelques instants après que la page se pose.
  // Au moment où l'utilisateur touche le menu, la page est déjà prête.
  function connectionIsLimited() {
    const c = navigator.connection;
    if (!c) return false;
    if (c.saveData) return true; // mode "économie de données" activé
    return /(^|-)2g$/.test(c.effectiveType || ""); // 2g / slow-2g
  }

  function prefetchVisibleLinks() {
    if (connectionIsLimited()) return; // on n'abuse pas du forfait data

    // 1) Liens du MENU (en-tête + tiroir burger) : on les précharge MÊME s'ils
    //    sont cachés (tiroir fermé sur mobile). Ce sont les destinations les
    //    plus probables => corrige la lenteur du menu burger sur téléphone.
    document.querySelectorAll(
      ".header__inline-menu a[href], #menu-drawer a[href], .menu-drawer a[href], .menu-drawer-container a[href]"
    ).forEach((a) => {
      if (shouldHandleLink(a)) prefetch(a.href);
    });

    // 2) Autres liens actuellement visibles à l'écran
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    let count = 0;

    document.querySelectorAll("a[href]").forEach((a) => {
      if (count >= 6) return;            // précharge un peu plus de destinations
                                         // visibles (menu + premières instrus)
      if (!shouldHandleLink(a)) return;
      const r = a.getBoundingClientRect();
      const inView = r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw;
      if (!inView) return;
      prefetch(a.href);
      count++;
    });
  }

  function scheduleIdlePrefetch() {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(prefetchVisibleLinks, { timeout: 2500 });
    } else {
      setTimeout(prefetchVisibleLinks, 1500);
    }
  }

  window.addEventListener("load", scheduleIdlePrefetch);
  window.addEventListener("softnav:loaded", () => setTimeout(scheduleIdlePrefetch, 100));

  // ── Trait blanc du menu qui SUIT la page active (avec animation) ──
  // En navigation instantanée, l'en-tête n'est pas rechargé : le trait blanc
  // resterait collé sur la page de départ. On met donc à jour, à chaque
  // navigation, le lien actif => le trait glisse en douceur vers la bonne page.
  function sasFollowActiveNav(targetPath) {
    const here = (targetPath || location.pathname).replace(/\/+$/, "") || "/";
    const links = document.querySelectorAll(".header__inline-menu .list-menu--inline > li > a[href]");
    links.forEach((a) => {
      let there;
      try { there = new URL(a.href).pathname.replace(/\/+$/, "") || "/"; }
      catch (e) { return; }
      const active = there === here;
      a.classList.toggle("sas-nav-active", active);
      const span = a.querySelector("span");
      if (span) span.classList.toggle("header__active-menu-item", active);
      if (active) a.setAttribute("aria-current", "page");
      else a.removeAttribute("aria-current");
    });
  }
  document.addEventListener("DOMContentLoaded", () => sasFollowActiveNav());
  // (Le déplacement du trait pendant la navigation est géré directement dans
  // navigate(), juste après le changement de page : il est synchronisé et
  // l'animation ne se coupe plus.)
})();
